import React, { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

export const useRowSelection = () => {
  const [selected, setSelected] = useState(() => new Set());

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = (ids) => setSelected(prev =>
    ids.every(id => prev.has(id)) ? new Set() : new Set(ids)
  );

  const clear = () => setSelected(new Set());

  return { selected, toggle, toggleAll, clear };
};

export const DeleteSelectedBar = ({ count, label, onConfirm, onClear }) => {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (count === 0) return null;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const deleted = await onConfirm();
      toast.success(`Deleted ${deleted} ${label}${deleted === 1 ? '' : 's'}`);
      setConfirming(false);
    } catch (error) {
      toast.error('Delete failed', { description: error.response?.data?.detail || 'Please try again.' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="admin-selection-bar" role="region" aria-live="polite" data-testid="delete-selected-bar">
      <span className="admin-selection-count" data-testid="selected-count">
        {count} {label}{count === 1 ? '' : 's'} selected
      </span>
      {confirming ? (
        <div className="admin-selection-actions">
          <span className="admin-selection-confirm-text">Permanently delete {count}? This cannot be undone.</span>
          <button
            type="button"
            className="admin-danger-btn"
            onClick={handleDelete}
            disabled={deleting}
            data-testid="confirm-delete-button"
          >
            <Trash2 size={16} /> {deleting ? 'Deleting...' : 'Yes, delete'}
          </button>
          <button
            type="button"
            className="admin-ghost-btn"
            onClick={() => setConfirming(false)}
            disabled={deleting}
            data-testid="cancel-delete-button"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="admin-selection-actions">
          <button
            type="button"
            className="admin-danger-btn"
            onClick={() => setConfirming(true)}
            data-testid="delete-selected-button"
          >
            <Trash2 size={16} /> Delete selected
          </button>
          <button type="button" className="admin-ghost-btn" onClick={onClear} data-testid="clear-selection-button">
            <X size={16} /> Clear
          </button>
        </div>
      )}
    </div>
  );
};
